import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from "mobx-react";
import { Grid, makeStyles } from '@material-ui/core';
import { formatDate } from '@roc/feature-utils';
import { DataGrid, GridColumnProps, SelectFilterComponent, SelectFloatingFilterComponent } from '@roc/ui';
import { noOpComparator, filterProps } from '@roc/ui/utils';
import InspectionsDashboardGridIdCell from './inspectionsDashboardGridIdCell';
import { InspectionReportStatusChip } from '../inspectionReportStatusChip';
import { InspectionStore } from '../../stores/inspectionStore';
import { InspectionPhotosStats } from '../inspectionPhotosStats';

const columns: GridColumnProps[] = [{
  field: 'inspectionId',
  headerName: 'Inspection ID',
  cellRenderer: 'inspectionIdCellRenderer',
  minWidth: 120,
  ...filterProps(),
}, {
  field: 'propertyAddress',
  headerName: 'Property Address',
  minWidth: 200,
  ...filterProps(),
}, {
  field: 'loanId',
  headerName: 'Loan ID',
  cellRenderer: 'loadIdCellRenderer',
  minWidth: 90,
  ...filterProps(),
}, {
  field: 'externalId2',
  headerName: 'Draw ID',
  cellRenderer: 'drawIdCellRenderer',
  minWidth: 90,
  ...filterProps(),
}, {
  field: 'inspectionStatus',
  headerName: 'Status',
  cellRenderer: 'statusCellRenderer',
  minWidth: 160,
  comparator: noOpComparator,
  filter: 'selectFilter',
  floatingFilter: true,
  floatingFilterComponent: 'selectFloatingFilter',
  floatingFilterComponentParams: {
    suppressFilterButton: true,
    options: [
      { label: 'Not Started', value: 'NOT_STARTED' },
      { label: 'Started', value: 'STARTED' },
      { label: 'Ready for Review', value: 'READY_FOR_REVIEW' },
      { label: 'Completed', value: 'COMPLETED' },
      { label: 'Rejected', value: 'REJECTED' },
    ],
  },
}, {
  field: 'createdDate',
  headerName: 'Date Created',
  cellRenderer: 'dateCellRenderer',
  minWidth: 200,
}, {
  field: 'successPhotosCount',
  headerName: 'Results',
  cellRenderer: 'resultsCellRenderer',
  width: 90,
  comparator: noOpComparator,
  sortable: false,
}, {
  field: 'lastUpdatedDate',
  headerName: 'Last Updated',
  cellRenderer: 'dateCellRenderer',
  minWidth: 200,
}];

const columnsInternalValuationInspection: GridColumnProps[] = [{
  field: 'inspectionId',
  headerName: 'Inspection ID',
  cellRenderer: 'inspectionIdCellRenderer',
  minWidth: 120,
  ...filterProps(),
}, {
  field: 'propertyAddress',
  headerName: 'Property Address',
  minWidth: 200,
  ...filterProps(),
}, {
  field: 'loanId',
  headerName: 'Loan ID',
  cellRenderer: 'loadIdCellRenderer',
  minWidth: 90,
  ...filterProps(),
}, {
  field: 'externalId2',
  headerName: 'Property ID',
  cellRenderer: 'drawIdCellRenderer',
  minWidth: 90,
  ...filterProps(),
}, {
  field: 'inspectionStatus',
  headerName: 'Status',
  cellRenderer: 'statusCellRenderer',
  minWidth: 160,
  comparator: noOpComparator,
  filter: 'selectFilter',
  floatingFilter: true,
  floatingFilterComponent: 'selectFloatingFilter',
  floatingFilterComponentParams: {
    suppressFilterButton: true,
    options: [
      { label: 'Not Started', value: 'NOT_STARTED' },
      { label: 'Started', value: 'STARTED' },
      { label: 'Ready for Review', value: 'READY_FOR_REVIEW' },
      { label: 'Completed', value: 'COMPLETED' },
      { label: 'Rejected', value: 'REJECTED' },
    ],
  },
}, {
  field: 'createdDate',
  headerName: 'Date Created',
  cellRenderer: 'dateCellRenderer',
  minWidth: 200,
}, {
  field: 'successPhotosCount',
  headerName: 'Results',
  cellRenderer: 'resultsCellRenderer',
  width: 90,
  comparator: noOpComparator,
  sortable: false,
}, {
  field: 'lastUpdatedDate',
  headerName: 'Last Updated',
  cellRenderer: 'dateCellRenderer',
  minWidth: 200,
}];

const useStyles = makeStyles(() => ({
  gridContainer: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    height: 'calc(85vh - 160px)',
    overflow: 'hidden',
  },
}));

interface InspectionsDashboardGridProps {
  store: InspectionStore;
  onOpenInspection?: (inspectionId: string) => void;
  isInternalValuationInspection?: boolean;
}

const InspectionsDashboardGrid = observer(({ store, onOpenInspection, isInternalValuationInspection }: InspectionsDashboardGridProps) => {
  const classes = useStyles();
  const { tab } = store;
  const [gridApi, setGridApi] = useState(null);
  const [currentGridStore, setCurrentGridStore] = useState(store.inspectionsDashboardDrawsGridStore);

  const frameworkComponents = {
    selectFilter: SelectFilterComponent,
    selectFloatingFilter: SelectFloatingFilterComponent,
    dateCellRenderer: ({ value }) => value ? formatDate(value, "MMM d, yyyy  hh:mm aaa") : '',
    statusCellRenderer: ({ value }) => value ? <InspectionReportStatusChip status={value} /> : '',
    inspectionIdCellRenderer: ({ value }) => value ? <InspectionsDashboardGridIdCell type='inspection' id={value} onClick={onOpenInspection} /> : '',
    loadIdCellRenderer: ({ value }) => value ? <InspectionsDashboardGridIdCell type='loan' id={value} /> : '',
    drawIdCellRenderer: ({ value, data }) => value && data.loanId ? <InspectionsDashboardGridIdCell type='draw' id={data.loanId} value={value} /> : '',
    resultsCellRenderer: params => {
      const data = params?.node?.data;
      return data ? (
        <InspectionPhotosStats
          totalPhotos={
            data.successPhotosCount +
            data.manuallyApprovedPhotosCount +
            data.failedPhotosCount
          }
          okPhotos={data.successPhotosCount}
          photosWithIssues={data.failedPhotosCount}
          manuallyApprovedPhotos={data.manuallyApprovedPhotosCount}
        />
      ) : null
    }
  };

  const onGridReady = useCallback(params => {
    setGridApi(params.api);
  }, []);

  const onTabChange = useCallback(() => {
    setCurrentGridStore(store.getCurrentGridStore());
  }, [store]);

  const onSortChanged = useCallback(params => {
    const sortModel = params.api.getSortModel();
    currentGridStore.setSortModel(sortModel);
  }, [currentGridStore]);

  const onFilterChanged = useCallback(params => {
    const filterModel = params.api.getFilterModel();
    currentGridStore.setFilterModel(filterModel);
  }, [currentGridStore]);

  useEffect(() => {
    onTabChange();
  }, [tab, onTabChange]);

  useEffect(() => {
    if (isInternalValuationInspection) {
      store.setInspectionType("INTERNAL_VALUATION");
    }
    currentGridStore.resetAndFetchGridData();
  }, [currentGridStore]);

  return (
    <Grid className={classes.gridContainer}>
      <DataGrid
        columns={isInternalValuationInspection ? columnsInternalValuationInspection : columns}
        rows={currentGridStore.gridData.data.rows}
        frameworkComponents={frameworkComponents}
        key={tab}
        onSortChanged={onSortChanged}
        onFilterChanged={onFilterChanged}
        isLoading={currentGridStore.isLoading}
        showFilters={currentGridStore.showFilters}
        sortModel={currentGridStore.sortModel}
        filterModel={currentGridStore.filterModel}
        paginationData={currentGridStore.gridData.meta}
        setPageNumber={currentGridStore.setPageNumber}
        // onRowSelected={onRowSelected}
        gridReady={onGridReady}
      />
    </Grid>
  );
});

export default InspectionsDashboardGrid;
